import React, { useState } from "react";
import { Link } from "react-router-dom";
import { ChevronDown, Search } from "lucide-react";
import SEO from "@/components/storefront/SEO";
import JsonLd, { faqSchema, breadcrumbSchema } from "@/components/storefront/JsonLd";

const FAQ = [
  {
    q: "How long does shipping take?",
    a: "Standard delivery ships within 1–2 business days and arrives in 5–7 business days for most destinations. Express options are available at checkout. Free standard delivery on orders over $75.",
  },
  {
    q: "What's your return policy?",
    a: "We offer 30-day hassle-free returns on all items in original, unused condition. Start a return from your order confirmation email or by contacting us — we'll email a pre-paid label within a day.",
  },
  {
    q: "Do you ship internationally?",
    a: "Not at the moment — we currently ship within the United States only. We deliver to all 50 states, including Alaska and Hawaii. Free standard shipping on orders over $75.",
  },
  {
    q: "Are your products sustainable?",
    a: "Where we can, yes. We prioritise natural, recycled, or low-impact materials, and our packaging is plastic-free and recyclable. Full material notes live on each product page.",
  },
  {
    q: "Can I change or cancel my order?",
    a: "If your order hasn't shipped, we can usually make changes. Email us at hello@luna-home.com with your order number within an hour of placing it and we'll do our best.",
  },
  {
    q: "Do you offer gift wrapping?",
    a: "Not yet — but we plan to. For now, every order arrives in our signature recyclable packaging, which doubles as a simple, beautiful gift wrap.",
  },
  {
    q: "How do I track my order?",
    a: "You'll receive a tracking link as soon as your parcel ships. If you haven't received it within 2 business days of ordering, check your spam folder or email us.",
  },
  {
    q: "Is checkout secure?",
    a: "Yes. Payments are processed over encrypted connections, and we never store card details on our servers. Your privacy matters to us.",
  },
];

export default function FAQ_Page() {
  const [open, setOpen] = useState(FAQ[0]?.q ?? null);
  const [q, setQ] = useState("");
  const filtered = FAQ.filter((x) => !q || x.q.toLowerCase().includes(q.toLowerCase()) || x.a.toLowerCase().includes(q.toLowerCase()));

  return (
    <div className="mx-auto max-w-3xl px-6 py-14 md:py-24" data-testid="faq-page">
      <SEO title="FAQ" description="Frequently asked questions about Luna. Creative Home orders, shipping, returns and more." />
      <JsonLd data={faqSchema(FAQ)} />
      <JsonLd data={breadcrumbSchema([
        { name: "Home", url: "/" },
        { name: "FAQ", url: "/faq" },
      ])} />
      <div className="mb-3 text-xs uppercase tracking-[0.3em] text-[#2D2823]">Help center</div>
      <h1 className="font-display text-5xl leading-tight md:text-[80px]">Frequently <span className="font-display-soft">asked</span></h1>
      <p className="mt-5 text-[#2D2823]">A few answers to our most common questions. Still need help? <Link to="/contact" className="text-[#7D8471] underline decoration-[#7D8471] underline-offset-4">Contact us</Link>.</p>

      <div className="relative mt-10">
        <Search size={16} className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-[#2D2823]" />
        <input
          placeholder="Search the FAQ..."
          value={q}
          onChange={(e) => setQ(e.target.value)}
          className="w-full rounded-full border border-[#DDD6C5] bg-white py-3.5 pl-11 pr-4 text-sm outline-none focus:border-[#1A1A1A]"
          data-testid="faq-search"
        />
      </div>

      <div className="mt-8 divide-y divide-[#DDD6C5] border-y border-[#DDD6C5]">
        {filtered.map((item, i) => {
          const isOpen = open === item.q;
          return (
            <div key={item.q}>
              <button onClick={() => setOpen(isOpen ? null : item.q)} className="flex w-full items-center justify-between gap-4 py-6 text-left" data-testid={`faq-${i}`}>
                <span className="font-display text-lg text-[#1A1A1A]">{item.q}</span>
                <ChevronDown size={18} className={`flex-shrink-0 transition-transform ${isOpen ? "rotate-180" : ""}`} />
              </button>
              {isOpen && <p className="pb-6 text-sm leading-relaxed text-[#2D2823]">{item.a}</p>}
            </div>
          );
        })}
        {filtered.length === 0 && <div className="py-12 text-center text-sm text-[#2D2823]">No matching question — try a different keyword or <Link to="/contact" className="text-[#7D8471] underline">ask us directly</Link>.</div>}
      </div>
    </div>
  );
}
